import React, { useEffect, useRef } from 'react';
import ArrowUpRight from 'lucide-react/dist/esm/icons/arrow-up-right';
import { detectPlatform, STORES } from './platform';
import { loadGsap, prefersReducedMotion } from './primitives';

/* ---------------------------------------------------------------------------
   Store buttons. A dark glass slab with the store's glyph and a two-line
   label, dealt in on load. With a mouse it is magnetic (GSAP quickTo, loaded
   after first paint) and a light sweep crosses it on hover; the glyph tilts
   and the slab presses in on tap. On a phone only that phone's store is shown,
   full width, with a slow emerald pulse.
   --------------------------------------------------------------------------- */

const APPLE_GLYPH = (
  <svg viewBox="0 0 384 512" className="h-[1.6rem] w-[1.6rem]" fill="currentColor" aria-hidden="true">
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
  </svg>
);

const PLAY_GLYPH = (
  <svg viewBox="0 0 24 24" className="h-[1.6rem] w-[1.6rem]" aria-hidden="true">
    <path d="M3.6 1.8 13.8 12 3.6 22.2c-.4-.2-.6-.6-.6-1.1V2.9c0-.5.2-.9.6-1.1z" fill="#00d7fe" />
    <path d="M17.2 8.6 13.8 12 3.6 1.8c.4-.2.8-.2 1.2 0l12.4 6.8z" fill="#00f076" />
    <path d="M17.2 15.4 4.8 22.2c-.4.2-.8.2-1.2 0L13.8 12l3.4 3.4z" fill="#ff3a44" />
    <path d="M21 12c0 .5-.3.9-.7 1.1l-3.1 2.3-3.4-3.4 3.4-3.4 3.1 2.3c.4.2.7.6.7 1.1z" fill="#ffd500" />
  </svg>
);

const COPY = {
  ios: { kicker: 'Download on the', name: 'App Store', glyph: APPLE_GLYPH },
  android: { kicker: 'Get it on', name: 'Google Play', glyph: PLAY_GLYPH },
};

function useMagnetic(ref) {
  useEffect(() => {
    const el = ref.current;
    if (!el || prefersReducedMotion() || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return undefined;
    let live = true;
    let cleanup = () => {};
    loadGsap().then(({ gsap }) => {
      if (!live || !gsap) return;
      const xTo = gsap.quickTo(el, 'x', { duration: 0.7, ease: 'elastic.out(1, 0.45)' });
      const yTo = gsap.quickTo(el, 'y', { duration: 0.7, ease: 'elastic.out(1, 0.45)' });
      const move = (e) => {
        const r = el.getBoundingClientRect();
        xTo((e.clientX - r.left - r.width / 2) * 0.22);
        yTo((e.clientY - r.top - r.height / 2) * 0.32);
      };
      const leave = () => {
        xTo(0);
        yTo(0);
      };
      el.addEventListener('pointermove', move, { passive: true });
      el.addEventListener('pointerleave', leave);
      cleanup = () => {
        el.removeEventListener('pointermove', move);
        el.removeEventListener('pointerleave', leave);
        gsap.killTweensOf(el);
      };
    });
    return () => {
      live = false;
      cleanup();
    };
  }, [ref]);
}

export function StoreButton({ platform, href, onClick, block = false, index = 0 }) {
  const ref = useRef(null);
  useMagnetic(ref);
  const { kicker, name, glyph } = COPY[platform];

  const className = `yl-store group relative isolate inline-flex h-[3.75rem] items-center gap-3 overflow-hidden rounded-[1.15rem] bg-[#0c0c0c] pl-[1.1rem] pr-6 text-left text-[#ffffff] ring-1 ring-inset ring-[#ffffff]/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#10b981] ${block ? 'yl-store--block w-full max-w-[26rem] justify-between pr-5' : ''}`;

  const body = (
    <>
      <span className="yl-store-shine" aria-hidden="true" />
      <span className="flex items-center gap-3">
        <span className="yl-store-glyph grid h-9 w-9 place-items-center" aria-hidden="true">
          {glyph}
        </span>
        <span className="flex flex-col leading-none">
          <span className="text-[0.625rem] font-medium tracking-[0.01em] text-[#ffffff]/65">{kicker}</span>
          <span className="mt-[0.3rem] text-[1.1875rem] font-semibold tracking-[-0.02em]">{name}</span>
        </span>
      </span>
      {block ? (
        <span className="yl-store-arrow grid h-9 w-9 place-items-center rounded-full bg-[#ffffff]/10" aria-hidden="true">
          <ArrowUpRight className="h-4 w-4" />
        </span>
      ) : null}
    </>
  );

  const style = { '--i': index };

  if (onClick) {
    return (
      <button ref={ref} type="button" onClick={onClick} className={className} style={style} aria-label={`${kicker} ${name}`}>
        {body}
      </button>
    );
  }
  return (
    <a
      ref={ref}
      href={href || STORES[platform].web}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      style={style}
      aria-label={`${kicker} ${name}`}
    >
      {body}
    </a>
  );
}

/**
 * The store row. A phone sees only its own store, full width; anything else
 * sees both, App Store first.
 */
export default function StoreButtons({ className = '' }) {
  const platform = detectPlatform();
  if (platform) {
    return (
      <div className={`flex w-full ${className}`}>
        <StoreButton platform={platform} block />
      </div>
    );
  }
  return (
    <div className={`flex flex-wrap items-center gap-3 ${className}`}>
      <StoreButton platform="ios" index={0} />
      <StoreButton platform="android" index={1} />
    </div>
  );
}
